import React, { useState, useEffect, useRef } from "react";
import suntoryLogo from "../images/suntoryLogo.png";
import drinkwareLogo from "../images/drinkaware.png";
import background from "../images/ageGate_desktop.jpg";

const AgeVerification = ({ onVerify }) => {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");
  const [young, setYoung] = useState("");
  const [loading, setLoading] = useState(true); // New loading state
  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const isVerified = localStorage.getItem("ageVerified");
    if (isVerified) {
      onVerify(false); // switch true to restore functionality
    }
    setLoading(false); // Set loading to false after checking
  }, [onVerify]);

  // Prevent rendering while checking localStorage
  if (loading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center bg-[#1d1d1b]"></div>
    );
  }
  const handleVerifyAge = () => {
    const currentDate = new Date();
    const [dayNum, monthNum, yearNum] = [day, month, year].map(Number); // Convert to numbers

    if (
      !dayNum ||
      !monthNum ||
      !yearNum ||
      isNaN(dayNum) ||
      isNaN(monthNum) ||
      isNaN(yearNum) ||
      year.length !== 4 // Check if the year has 4 digits
    ) {
      // setError("Please make sure all fields are input correctly.");
      setError("Please enter a valid date.");
      setYoung("");
      return;
    }

    // Check if the entered date is valid in the context of the month and year
    const isValidDate = isValidDateInMonth(yearNum, monthNum, dayNum);

    if (
      !isValidDate ||
      yearNum > currentDate.getFullYear() ||
      (yearNum === currentDate.getFullYear() &&
        monthNum > currentDate.getMonth() + 1) ||
      (yearNum === currentDate.getFullYear() &&
        monthNum === currentDate.getMonth() + 1 &&
        dayNum > currentDate.getDate())
    ) {
      setError("Please enter a valid date.");
      setYoung("");
      return;
    }

    let age = currentDate.getFullYear() - yearNum;
    if (
      currentDate.getMonth() < monthNum - 1 ||
      (currentDate.getMonth() === monthNum - 1 &&
        currentDate.getDate() < dayNum)
    ) {
      age--;
    }

    if (age >= 18) {
      setError("");
      setYoung("");
      localStorage.setItem("ageVerified", "true");
      onVerify(true);
    } else {
      setYoung("Unfortunately, you are not old enough.");
      setError("");
    }
  };

  // Helper function to check if the entered day is valid for the given month and year
  const isValidDateInMonth = (year, month, day) => {
    const daysInMonth = new Date(year, month, 0).getDate(); // Get the last day of the month
    return day >= 1 && day <= daysInMonth;
  };

  const handleInputChange = (
    setter,
    value,
    maxLength,
    nextRef,
    event = null
  ) => {
    if (/^\d*$/.test(value)) {
      let newValue = value;
      const currentYear = new Date().getFullYear(); // Get current year dynamically
      const currentYearStr = currentYear.toString();
      const currentYearSuffix = parseInt(currentYearStr.slice(2)); // Last two digits of the current year

      setError("");
      // Handle day input
      if (setter === setDay) {
        // ✅ Allow clearing input when user deletes
        if (newValue === "") {
          setter(""); // Clear input
          return;
        }

        if (/^\d{1,2}$/.test(newValue)) {
          const firstDigit = newValue[0];

          // If only one digit is entered
          if (newValue.length === 1) {
            if (["0", "1", "2", "3"].includes(firstDigit)) {
              setter(newValue); // ✅ Allow input and wait for second digit
              return;
            } else if (parseInt(firstDigit, 10) >= 4) {
              setter(newValue); // ✅ Allow input
              monthRef.current?.focus(); // Auto-tab for 4+
              return;
            }
          }

          // If two digits are entered, validate full number
          const day = parseInt(newValue, 10);
          if (day >= 1 && day <= 31) {
            setter(newValue); // ✅ Update state with valid input
          } else {
            return; // Invalid day, do nothing
          }
        } else {
          return; // Invalid input format, do nothing
        }
      }

      // Handle day input
      if (setter === setMonth) {
        // ✅ Allow clearing input when user deletes
        if (newValue === "") {
          setter(""); // Clear input
          return;
        }

        if (/^\d{1,2}$/.test(newValue)) {
          const firstDigit = newValue[0];

          // If only one digit is entered
          if (newValue.length === 1) {
            if (["0", "1"].includes(firstDigit)) {
              setter(newValue); // ✅ Allow input and wait for second digit
              return;
            } else if (parseInt(firstDigit, 10) >= 2) {
              setter(newValue); // ✅ Allow input
              yearRef.current?.focus(); // Auto-tab for 4+
              return;
            }
          }

          // If two digits are entered, validate full number
          const day = parseInt(newValue, 10);
          if (day >= 1 && day <= 12) {
            setter(newValue); // ✅ Update state with valid input
          } else {
            return; // Invalid day, do nothing
          }
        } else {
          return; // Invalid input format, do nothing
        }
      }

      // Handle year input
      if (setter === setYear) {
        if (newValue.length > 4) return; // Ensure max length of 4 digits

        // Enforce the rules based on the digits already entered
        if (newValue.length === 1) {
          if (!/[1-2]/.test(newValue)) return; // First digit can only be 1 or 2
        }

        if (newValue.length === 2) {
          const firstDigit = newValue[0];
          const secondDigit = newValue[1];

          // If first digit is 1, second digit must be 9
          if (firstDigit === "1" && secondDigit !== "9") {
            return;
          }

          // If first digit is 2, second digit must be 0
          if (firstDigit === "2" && secondDigit !== "0") {
            return;
          }
        }

        if (newValue.length === 3) {
          const secondDigit = newValue[1];
          const thirdDigit = newValue[2];

          // If second digit is 9, third digit can be any number (0-9)
          if (secondDigit === "9" && !/[0-9]/.test(thirdDigit)) {
            return; // Allow any digit from 0-9
          }

          // If second digit is 0, third digit must be 0-2 unless we reach a year like 2030
          if (secondDigit === "0" && !/[0-2]/.test(thirdDigit)) {
            // We need to dynamically check based on the current year
            const maxThirdDigit =
              currentYearSuffix >= 30 ? currentYearSuffix - 30 : 2;
            if (parseInt(thirdDigit) > maxThirdDigit) {
              return;
            }
          }
        }

        // If input is complete (4 digits), check if it's within the valid year range
        if (newValue.length === 4) {
          const enteredYear = Number(newValue);

          // Allow typing the year, but reject if it's less than 1900 or greater than the current year
          if (enteredYear < 1900 || enteredYear > currentYear) {
            return; // Reject years outside the allowed range
          }
        }
      }

      setter(newValue);

      // Move focus to the next input if max length is reached
      if (newValue.length === maxLength && nextRef) {
        nextRef.current.focus();
        nextRef.current.select();
      }
    }
  };

  return (
    <div
      id="age-gate"
      className="h-full w-full fixed z-50 flex justify-center items-center flex-col"
      style={{
        backgroundImage: `url(${background})`, // Set background image
        backgroundSize: "cover", // Make it cover the entire screen
        backgroundPosition: "center", // Center the background image
        backgroundRepeat: "no-repeat", // Don't repeat the background
      }}
    >
      <div className=" z-10  rounded-lg md:w-[716px]  xs:w-[90%]   md:mx-[15px] sm:mx-[38px] mob2:mx-[28px] xs:mx-[18px] flex flex-col items-center text-[#ffe52c]">
        <img
          src={suntoryLogo}
          className="md:mt-[50px] xs:mt-[50px] mx-auto md:w-[210px] mob2:w-[210px] xs:w-[200px]"
          alt="logo"
        />
        <p className="md:mt-[60px]  sm:mt-[60px] xs:mt-[40px] font-brandonGrotesque text-center  mx-auto md:text-[21px]  sms:text-[21px] sm:text-[21px] mob2:text-[21px] xs:text-[21px] sm:leading-8 xs:leading-6 tracking-tight md:w-[447px] sm:w-[285px] mob2:w-[285px] mob:w-[285px] xs:w-[240px]">
          To visit lacasera.co.uk you must be over the age of 18.
        </p>
        {!young && (
          <div className="flex justify-center space-x-2 md:mt-[60px] sm:mt-[40px] xs:mt-[30px]  x904:text-[130px]  md:text-[100px]  sm:text-[78px]  mob2:text-[60px] xs:text-[40px] leading-none  tracking-wider">
            <input
              type="tel" // Change this from "text" to "tel"
              inputMode="numeric" // Ensures numeric input mode
              pattern="[0-9]*" // Ensures only numbers are accepted
              placeholder="DD"
              ref={dayRef}
              value={day}
              onChange={(e) =>
                handleInputChange(setDay, e.target.value, 2, monthRef, e)
              }
              maxLength={2}
              onFocus={(e) => e.target.select()} // Selects all text when input is focused
              className={`${
                year || month || day
                  ? " x904:w-[200px] md:w-[150px] sm:w-[120px] mob2:w-[90px] xs:w-[60px]"
                  : " x904:w-[154px] md:w-[119px] sm:w-[95px] mob2:w-[73px]  xs:w-[48px]"
              } font-megascopeVariable  text-center outline-none text-[#ffe52c] bg-transparent appearance-none placeholder-[#ffe52c] flex items-center`}
            />
            <input
              type="tel" // Change this from "text" to "tel"
              inputMode="numeric" // Ensures numeric input mode
              pattern="[0-9]*" // Ensures only numbers are accepted
              placeholder="MM"
              ref={monthRef}
              value={month}
              onChange={(e) =>
                handleInputChange(setMonth, e.target.value, 2, yearRef, e)
              }
              maxLength={2}
              onFocus={(e) => e.target.select()} // Selects all text when input is focused
              className={`${
                year || month || day
                  ? " x904:w-[200px] md:w-[150px] sm:w-[120px] mob2:w-[90px] xs:w-[60px]"
                  : "x904:w-[154px] md:w-[119px] sm:w-[95px] mob2:w-[73px]  xs:w-[48px]"
              } font-megascopeVariable  text-center outline-none text-[#ffe52c] bg-transparent appearance-none placeholder-[#ffe52c] flex items-center`}
            />

            <input
              type="tel" // Change this from "text" to "tel"
              inputMode="numeric" // Ensures numeric input mode
              pattern="[0-9]*" // Ensures only numbers are accepted
              placeholder="YYYY"
              ref={yearRef}
              value={year}
              onChange={(e) =>
                handleInputChange(setYear, e.target.value, 4, null, e)
              }
              maxLength={4}
              onFocus={(e) => e.target.select()} // Highlights all text when clicked
              className={`${
                year || month || day
                  ? " x904:w-[390px] md:w-[300px] sm:w-[230px] mob2:w-[174px] xs:w-[118px]"
                  : " x904:w-[216px]  md:w-[169px] sm:w-[130px] mob2:w-[100px] xs:w-[67px]"
              } font-megascopeVariable  text-center outline-none text-[#ffe52c] bg-transparent appearance-none placeholder-[#ffe52c] flex items-center`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerifyAge();
                }
              }}
            />
          </div>
        )}
        {young && (
          <p className="text-[#ffe52c] mt-10 mob2:w-[90%] xs:w-[70%] font-brandonGrotesque font-extrabold ">
            {young}
          </p>
        )}
        {error && (
          <p className="text-[#ffe52c] mt-0 font-brandonGrotesque font-extrabold xs:w-[230px] mob2:w-[100%]">
            {error}
          </p>
        )}
        <button
          onClick={handleVerifyAge}
          disabled={!day || !month || !year || year.length !== 4 || error}
          className={`flex items-center justify-center mx-auto border border-[#fedd00] rounded-[32px]  
            md:w-[350px] sm:w-[202px] mob2:w-[202px] xs:w-[202px] 
            text-[#fedd00] md:mb-[0px] xs:mb-[0px] 
            ${
              error
                ? "md:mt-[15px] mob2:mt-[15px] xs:mt-[5px]"
                : "md:mt-[39px] mob2:mt-[39px] xs:mt-[53px]"
            } 
            ${young ? "hidden" : ""} 
            ${
              !day || !month || !year || year.length !== 4 || error
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
        >
          <p className="text-center md:py-3 sm:py-3 xs:py-2 md:text-[20px] sm:text-[12px] mob2:text-[12px] xs:text-[12px] font-brandonGrotesque  tracking-tight">
            I AM OF LEGAL DRINKING AGE
          </p>
        </button>
      </div>
      <div
        id="disclaimer"
        style={{ display: "block" }}
        className=" relative mx-auto w-full  md:mt-[40px] sms:mt-[20px] xs:mt-[20px]  "
      >
        <p className="relative md:mt-[0px] sms:mt-[0px] xs:mt-[0px] font-brandonGrotesqueMd  tracking-[-0.04em] text-center  x904:text-[14px] sms:text-[14px] sm:text-[14px] mob2:text-[14px]  xs:text-[14px] leading-tight text-[#ffe52c] xs:mx-auto min-[1220px]:w-[494px] x904:w-[494px] md:w-[494px] sm:w-[312px] mob2:w-[312px] xs:w-[211px]">
          By entering this site, you are agreeing to our privacy and cookies
          policy. Please enjoy responsibly. Do not share or forward this website
          or its content to anyone under&nbsp;18.
          <br />
          <br />
        </p>
        <div className="flex items-center x902:mx-0 xs:mx-auto justify-center">
          {/* <span className="relative pt-[6px] font-barlowBold font-normal x904:text-[20px] sms:text-[20px] sm:text-[20px] mob2:text-[20px] xs:text-[20px] leading-none text-[#ffe52c]">
            <b>18+</b>&nbsp;
          </span> */}
          <img
            src={drinkwareLogo}
            className="mt-1 relative min-[1220px]:w-[164px] x904:w-[164px] sms:w-[164px] sm:w-[164px] mob2:w-[164px] xs:w-[160px]"
            alt="drinkware-Logo"
          />
        </div>
      </div>
    </div>
  );
};

export default AgeVerification;
